import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import cogoToast from "cogo-toast";
import { getJobListAction } from "../../../redux/actions/jobActions";
import {
  generateDynamicSitemap,
  downloadSitemap,
} from "../../../utils/dynamicSitemapGenerator";
import "./index.css";

const GoogleJobsIndex = () => {
  const dispatch = useDispatch();
  const { jobList } = useSelector((store) => store);
  const [sectionLoading, setSectionLoading] = useState(true);
  const [isGeneratingSitemap, setIsGeneratingSitemap] = useState(false);

  useEffect(() => {
    Promise.resolve(getJobListAction(dispatch)).finally(() => {
      setSectionLoading(false);
    });
  }, [dispatch]);

  const handleGenerateSitemap = async () => {
    setIsGeneratingSitemap(true);
    try {
      const allJobs =
        jobList.jobList && jobList.jobList[0] ? jobList.jobList[0] : [];
      if (allJobs.length === 0) {
        cogoToast.warn("No jobs found to include in sitemap");
        setIsGeneratingSitemap(false);
        return;
      }
      const sitemapContent = await generateDynamicSitemap(allJobs);
      downloadSitemap(sitemapContent);
      cogoToast.success("Sitemap generated and download started!");
    } catch (error) {
      cogoToast.error("Failed to generate sitemap");
    } finally {
      setIsGeneratingSitemap(false);
    }
  };

  // Helper to format salary
  const formatSalary = (salary) => {
    if (!salary) return 0;
    const cleaned = salary.toString().replace(/[^\d.]/g, "");
    return parseFloat(cleaned) || 0;
  };

  // Helper to format job type for Google enums
  const formatJobType = (type) => {
    if (!type) return "FULL_TIME";
    const t = type.toUpperCase().replace(/[\s-]/g, "_");
    const validTypes = [
      "FULL_TIME",
      "PART_TIME",
      "CONTRACTOR",
      "TEMPORARY",
      "INTERN",
      "VOLUNTEER",
      "PER_DIEM",
      "OTHER",
    ];
    return validTypes.includes(t) ? t : "FULL_TIME";
  };

  // Helper to calculate expiry date
  const getExpiryDate = (postDate) => {
    if (!postDate) return undefined;
    try {
      const date = new Date(postDate);
      date.setDate(date.getDate() + 60); // 60 days default
      return date.toISOString();
    } catch (e) {
      return undefined;
    }
  };

  // Generate structured data for Google Jobs
  const generateJobStructuredData = (jobs) => {
    if (!jobs || !jobs.length) return null;

    const jobPostings = jobs.map((job) => ({
      "@type": "JobPosting",
      title: job.role_title,
      description: job.about_company,
      identifier: {
        "@type": "PropertyValue",
        name: job.campany_name,
        value: job.id,
      },
      datePosted: job.date_created,
      validThrough: getExpiryDate(job.date_created),
      employmentType: formatJobType(job.job_type),
      directApply: true,
      totalJobOpenings: 1,
      hiringOrganization: {
        "@type": "Organization",
        name: job.campany_name,
        sameAs: process.env.REACT_APP_WEB_URL,
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: job.location,
          addressRegion: "NG",
          addressCountry: "NG",
        },
      },
      baseSalary: {
        "@type": "MonetaryAmount",
        currency: "NGN",
        value: {
          "@type": "QuantitativeValue",
          value: formatSalary(job.salary),
          unitText: "YEAR",
        },
      },
      industry: job.industry,
      skills: job.skills_required ? JSON.parse(job.skills_required) : [],
      url: `${process.env.REACT_APP_WEB_URL}/job-lists-details/${job.id}/apply/no-ref`,
    }));

    return {
      "@context": "https://schema.org/",
      "@type": "JobPosting",
      "@graph": jobPostings,
    };
  };

  const jobs = jobList.jobList && jobList.jobList[0] ? jobList.jobList[0] : [];
  const structuredData = generateJobStructuredData(jobs);

  return (
    <>
      <Helmet>
        <title>Jobs at workbrook - Find Your Next Career Opportunity</title>
        <meta
          name="description"
          content="Discover the latest job opportunities at workbrook. Browse through various positions across different industries and find your perfect career match."
        />
        <meta
          name="keywords"
          content="jobs, careers, employment, workbrook, hiring, job search"
        />
        <meta
          property="og:title"
          content="Jobs at workbrook - Find Your Next Career Opportunity"
        />
        <meta
          property="og:description"
          content="Discover the latest job opportunities at workbrook. Browse through various positions across different industries."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${process.env.REACT_APP_WEB_URL}/jobs`}
        />
        <link rel="canonical" href={`${process.env.REACT_APP_WEB_URL}/jobs`} />
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      {sectionLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="google-jobs-index">
          <header className="jobs-header">
            <div className="container">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1>Current Job Openings</h1>
                  <p>
                    Explore exciting career opportunities and join our growing
                    team
                  </p>
                  <p className="gj-match-preview-line">
                    <Link to="/match-score">
                      Check how your resume matches a job — free AI preview
                    </Link>
                  </p>
                </div>
                <div className="sitemap-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerateSitemap}
                    disabled={isGeneratingSitemap}
                  >
                    {isGeneratingSitemap ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i
                          className="fa fa-sitemap mr-2"
                          aria-hidden="true"
                        ></i>
                        Download Dynamic Sitemap
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </header>

          <main className="jobs-content">
            <div className="container">
              <div className="jobs-list">
                {jobs.length > 0 ? (
                  jobs.map((item, index) => (
                    <article
                      className="job-card"
                      key={index}
                      itemScope
                      itemType="https://schema.org/JobPosting"
                    >
                      <div className="job-card-body">
                        <div className="job-main-content">
                          <header className="job-header">
                            <h2 itemProp="title">
                              <a
                                href={`${process.env.REACT_APP_WEB_URL}/job-lists-details/${item.id}/apply/no-ref`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="job-title-link"
                                itemProp="url"
                              >
                                {item.role_title}
                              </a>
                            </h2>
                          </header>

                          <div
                            className="job-meta"
                            itemProp="hiringOrganization"
                            itemScope
                            itemType="https://schema.org/Organization"
                          >
                            <div className="job-meta-item">
                              <i
                                className="fa fa-building"
                                aria-hidden="true"
                              ></i>
                              <span itemProp="name">{item.campany_name}</span>
                            </div>
                            <div className="job-meta-item">
                              <i
                                className="fa fa-certificate"
                                aria-hidden="true"
                              ></i>
                              <span itemProp="industry">{item.industry}</span>
                            </div>
                            <div className="job-meta-item">
                              <i
                                className="fa fa-calendar"
                                aria-hidden="true"
                              ></i>
                              <span itemProp="employmentType">
                                {item.job_type}
                              </span>
                            </div>
                          </div>

                          <div
                            className="job-description"
                            itemProp="description"
                          >
                            <p
                              dangerouslySetInnerHTML={{
                                __html:
                                  item.about_company.substring(0, 600) + " ...",
                              }}
                            />
                            <a
                              href={`${process.env.REACT_APP_WEB_URL}/job-lists-details/${item.id}/apply/no-ref`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="apply-now-btn"
                            >
                              Apply Now
                            </a>
                          </div>

                          <div className="job-location-date">
                            <div
                              itemProp="jobLocation"
                              itemScope
                              itemType="https://schema.org/Place"
                            >
                              <span
                                itemProp="address"
                                itemScope
                                itemType="https://schema.org/PostalAddress"
                              >
                                <span itemProp="addressLocality">
                                  {item.location}
                                </span>
                              </span>
                            </div>
                            <span className="job-date">
                              Date Posted:{" "}
                              <time
                                itemProp="datePosted"
                                dateTime={item.date_created}
                              >
                                {item.date_created}
                              </time>
                            </span>
                          </div>

                          {item.skills_required && (
                            <div className="job-skills">
                              <h3>Required Skills:</h3>
                              <ul className="skills-list" itemProp="skills">
                                {JSON.parse(item.skills_required).map(
                                  (skill, skillIndex) => (
                                    <li key={skillIndex} className="skill-tag">
                                      {skill}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="no-jobs-message">
                    <h2>No job openings available at the moment</h2>
                    <p>Please check back later for new opportunities.</p>
                  </div>
                )}
              </div>

              {jobs.length > 0 && (
                <div className="jobs-footer">
                  <p>
                    Showing {jobs.length} job{jobs.length !== 1 ? "s" : ""}
                    {jobList.total_item_count &&
                      ` of ${jobList.total_item_count} total positions`}
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default GoogleJobsIndex;
